import {
    computed,
    defineComponent,
    ref,
    type PropType,
} from "vue";
import {
    buildTeamDiscordProfileUrl,
    buildTeamSocialProfileUrl,
    createFallbackPublicTeamView,
    formatTeamMemberTemplate,
    isPublicTeamView,
    type PublicTeamMember,
    type PublicTeamView,
    type TeamLocale,
    type TeamSocialPlatform,
} from "@logscord/public-site-config";
import {
    PublicFooter,
    PublicNavbar,
} from "./PublicShell.js";
import { useAppState } from "../../appState.js";
import { useVuePublicRouteData } from "@logscord/public-site/adapters/vue";
import { useLocale } from "../../locales/i18n.js";
import { usePublicReveal } from "./usePublicReveal.js";


//////////////////////////////////////////////////
//  TEAM
//////////////////////////////////////////////////


type TeamSocialName = "discord" | "linkedin" | "github" | "npm" | "portfolio";

type TeamSocialLink = {
    name  : TeamSocialName;
    label : string;
    href  : string;
};

function getInitials(name: string) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? "")
        .join("");
}

function getTeamSocialLabel(
    name: Exclude<TeamSocialName, "discord">,
    portfolioLabel: string,
) {
    switch (name) {
        case "linkedin":
            return "LinkedIn";
        case "github":
            return "GitHub";
        case "npm":
            return "npm";
        case "portfolio":
            return portfolioLabel;
    }
}

function formatTeamSocialHandle(handle: string) {
    return handle.startsWith("@") ? handle : `@${handle}`;
}

const TeamSocialIcon = defineComponent({
    props: {
        name: {
            type     : String as PropType<TeamSocialName>,
            required : true,
        },
    },

    setup(props) {
        return () => {
            if (props.name === "github")
                return (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.11.79-.25.79-.56v-2.01c-3.2.7-3.87-1.54-3.87-1.54-.52-1.33-1.28-1.68-1.28-1.68-1.05-.72.08-.71.08-.71 1.16.08 1.77 1.19 1.77 1.19 1.03 1.76 2.7 1.25 3.36.96.1-.75.4-1.25.73-1.54-2.55-.29-5.23-1.28-5.23-5.68 0-1.25.45-2.28 1.19-3.08-.12-.29-.52-1.46.11-3.04 0 0 .97-.31 3.17 1.18a10.9 10.9 0 0 1 5.78 0c2.2-1.49 3.16-1.18 3.16-1.18.63 1.58.23 2.75.11 3.04.74.8 1.19 1.83 1.19 3.08 0 4.41-2.69 5.38-5.25 5.67.42.36.78 1.06.78 2.14v3.18c0 .31.21.68.8.56A11.51 11.51 0 0 0 23.5 12C23.5 5.65 18.35.5 12 .5Z" />
                    </svg>
                );

            if (props.name === "discord")
                return (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M19.54 5.34A18.8 18.8 0 0 0 14.92 3.9l-.22.44a17.3 17.3 0 0 1 4.14 2.06 13.42 13.42 0 0 0-12.37 0 16.6 16.6 0 0 1 4.14-2.06l-.22-.44a18.8 18.8 0 0 0-4.63 1.44C2.83 9.72 2.03 13.99 2.43 18.2a18.9 18.9 0 0 0 5.67 2.87l.69-.94a12.2 12.2 0 0 1-1.09-.52c.31-.12.62-.25.91-.4a13.44 13.44 0 0 0 6.78 0c.3.15.6.28.91.4-.35.19-.71.36-1.09.52l.69.94a18.85 18.85 0 0 0 5.68-2.87c.47-4.87-.8-9.1-2.04-12.86ZM8.52 15.62c-1.1 0-2-1.01-2-2.25s.88-2.26 2-2.26c1.12 0 2.02 1.02 2 2.26 0 1.24-.88 2.25-2 2.25Zm6.96 0c-1.1 0-2-1.01-2-2.25s.88-2.26 2-2.26c1.12 0 2.02 1.02 2 2.26 0 1.24-.88 2.25-2 2.25Z" />
                    </svg>
                );

            if (props.name === "linkedin")
                return (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12ZM7.12 20.45H3.55V9h3.57v11.45ZM22.23 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45C23.2 24 24 23.23 24 22.27V1.73C24 .77 23.2 0 22.22 0h.01Z" />
                    </svg>
                );

            if (props.name === "npm")
                return (
                    <svg viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M0 16V0h16v16H0ZM3 3v10h5V5h3v8h2V3H3Z" />
                    </svg>
                );

            return <i class="material-symbols-outlined" aria-hidden="true">language</i>;
        };
    },
});

const TeamMemberCard = defineComponent({
    props: {
        member: {
            type     : Object as PropType<PublicTeamMember>,
            required : true,
        },
        locale: {
            type     : String as PropType<TeamLocale>,
            required : true,
        },
        syncing: {
            type    : Boolean,
            default : false,
        },
    },

    setup(props) {
        const { i18n } = useLocale();
        const avatarFailed = ref(false);
        const copy = computed(() => props.member.copy[props.locale]);
        const socialLinks = computed<TeamSocialLink[]>(() => {
            const links: TeamSocialLink[] = [];
            const discord = props.member.discord;

            if (discord.userId)
                links.push({
                    name  : "discord",
                    label : discord.handle ? `Discord · ${discord.handle}` : "Discord",
                    href  : buildTeamDiscordProfileUrl(discord.userId),
                });

            for (const name of [ "linkedin", "github", "npm" ] as const satisfies readonly TeamSocialPlatform[]) {
                const profile = props.member.socials[name];
                const networkLabel = getTeamSocialLabel(name, i18n("team.social.portfolio"));

                if (profile)
                    links.push({
                        name,
                        label : `${networkLabel} · ${formatTeamSocialHandle(profile.handle)}`,
                        href  : buildTeamSocialProfileUrl(name, profile.handle),
                    });
            }

            if (props.member.socials.portfolio)
                links.push({
                    name  : "portfolio",
                    label : props.member.socials.portfolio.label,
                    href  : props.member.socials.portfolio.url,
                });

            return links;
        });

        return () => (
            <article class="team-member-card" data-reveal="member-card">
                <div class="team-member-identity">
                    <div class={{ "team-member-avatar": true, "team-member-avatar-syncing": props.syncing }}>
                        {props.member.discord.avatarUrl && !avatarFailed.value
                            ? (
                                <img
                                    src={props.member.discord.avatarUrl}
                                    alt={i18n("team.avatar.alt", props.member.discord.displayName)}
                                    width="184"
                                    height="184"
                                    onError={() => {
                                        avatarFailed.value = true;
                                    }}
                                />
                            )
                            : <span aria-hidden="true">{getInitials(props.member.name)}</span>}
                    </div>

                    <div class="team-member-heading">
                        <div class="team-member-badges">
                            <span class="team-member-status">
                                <i class="material-symbols-outlined" aria-hidden="true">verified</i>
                                {copy.value.status}
                            </span>
                            <span class="team-member-location">
                                <i class="material-symbols-outlined" aria-hidden="true">public</i>
                                {copy.value.location}
                            </span>
                        </div>

                        <h2>{props.member.discord.displayName}</h2>
                        {props.member.discord.handle && <span class="team-member-handle">{props.member.discord.handle}</span>}
                        <p class="team-member-role">{copy.value.role}</p>

                        {socialLinks.value.length > 0 && (
                            <div class="team-member-socials" aria-label={i18n("team.social.aria", props.member.name)}>
                                {socialLinks.value.map((link) => (
                                    <a
                                        href={link.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={link.label}
                                        title={link.label}
                                        key={link.name}
                                    >
                                        <TeamSocialIcon name={link.name} />
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div class="team-member-story">
                    <p class="team-member-description">
                        {formatTeamMemberTemplate(copy.value.description, props.member.discord.displayName)}
                    </p>

                    <blockquote>
                        <i class="material-symbols-outlined" aria-hidden="true">format_quote</i>
                        <p>{copy.value.message}</p>
                    </blockquote>

                    <div class="team-member-motto">
                        <span>{i18n("team.motto.label")}</span>
                        <strong>{copy.value.motto}</strong>
                    </div>
                </div>
            </article>
        );
    },
});

export default defineComponent({
    setup() {
        const { i18n } = useLocale();
        const appState = useAppState();
        const publicRouteData = useVuePublicRouteData();
        const pageElement = ref<HTMLElement | null>(null);
        const team = computed<PublicTeamView>(() => {
            const value = publicRouteData.getData("publicTeam");

            return isPublicTeamView(value)
                ? value
                : createFallbackPublicTeamView();
        });
        const syncing = computed(() => (
            publicRouteData.state.value?.status === "loading"
        ));
        const failed = computed(() => (
            publicRouteData.state.value?.status === "unavailable"
            || publicRouteData.state.value?.status === "not-found"
        ));
        const teamLocale = computed<TeamLocale>(() => (
            appState.locale.value.toLowerCase().startsWith("fr")
                ? "fr-FR"
                : "en-US"
        ));
        const primaryDisplayName = computed(() => (
            team.value.members[0]?.discord.displayName ?? ""
        ));
        usePublicReveal(pageElement);

        return () => (
            <div class="landing-page team-page" ref={pageElement}>
                <PublicNavbar />

                <main>
                    <div class="team-hero" data-reveal="hero-copy">
                        <div class="team-hero-copy">
                            <span class="landing-kicker">{i18n("team.hero.kicker")}</span>
                            <h1>{i18n("team.hero.title")}</h1>
                            <p>
                                {formatTeamMemberTemplate(
                                    i18n("team.hero.description"),
                                    primaryDisplayName.value,
                                )}
                            </p>
                        </div>

                        <div class="team-hero-aside" aria-label={i18n("team.current.aria")}>
                            <span class="team-live-dot" />
                            <strong>{i18n("team.current.title")}</strong>
                            <span>{i18n("team.current.description")}</span>
                        </div>
                    </div>

                    <div class="team-members" aria-live="polite">
                        {team.value.members.map((member) => (
                            <TeamMemberCard
                                member={member}
                                locale={teamLocale.value}
                                syncing={syncing.value}
                                key={member.id}
                            />
                        ))}
                    </div>

                    {(syncing.value || failed.value) && (
                        <div class="team-sync-state" role="status">
                            {syncing.value && (
                                <>
                                    <span class="team-sync-spinner" />
                                    {i18n("team.sync.loading")}
                                </>
                            )}
                            {failed.value && (
                                <>
                                    <i class="material-symbols-outlined" aria-hidden="true">cloud_off</i>
                                    {i18n("team.sync.fallback")}
                                </>
                            )}
                        </div>
                    )}

                    <div class="team-signature" data-reveal="section-heading">
                        <span>{i18n("team.signature.kicker")}</span>
                        <div>
                            <strong>Powerful observability.</strong>
                            {" "}
                            <strong>Responsible by design.</strong>
                        </div>
                    </div>
                </main>

                <PublicFooter />
            </div>
        );
    },
});
